import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { schedulingApi } from "../api/scheduling";
import { useState } from "react";


interface UseActivityTodosProps {
    projectId: string | undefined;
    activityId: number | undefined;
}

export const useActivityTodos = ({projectId, activityId}: UseActivityTodosProps) => {
    const qc = useQueryClient();

    const [message, setMessage] = useState<{
        type: "success" | "error",
        text: string
    } | null>(null)

    // FETCH TODOS
    const { data, isPending, isFetching, isError, refetch} = useQuery({
        queryKey: ["activity-todos", projectId, activityId],
        queryFn: async () => {
            return await schedulingApi.getActivityTodos(projectId, activityId);
        },
        enabled: !!projectId && !!activityId,
        });

    // CREATE TODO
    const createTodoMutation  = useMutation({
        mutationFn: async (payload: { description: string}) => {
            return await schedulingApi.createActivityTodo(projectId!, activityId!, payload);
        },

        onSuccess: async () => {
            setMessage({
                type: "success",
                text: "Todo added successfully"
            })
            qc.invalidateQueries({
                queryKey: ["activity-todos", projectId, activityId]
            })
        },

        onError: (error: any) => {
            setMessage({
                type: "error",
                text: error?.response?.data?.data?.message || "Failed to add todo"
            })
        }
    });

    // UPDATE TODO status
    const updateTodoMutation  = useMutation({
        mutationFn: async ({ todoId, payload }: { todoId: number, payload: { status: number}}) => {
            return await schedulingApi.updateActivityTodo(projectId!, activityId!, todoId, payload);
        },

        onSuccess: async () => {
            setMessage({
                type: "success",
                text: "Todo updated successfully"
            })
            qc.invalidateQueries({
                queryKey: ["activity-todos", projectId, activityId]
            })
        },

        onError: (error: any) => {
            setMessage({
                type: "error",
                text: "Failed to update todo"
            })
        }
    });

    return {
        todos: data?.data?.todos ?? [],
        activityName: data?.data?.activity_name ?? "",
        projectTreeId: data?.data?.project_tree_id ?? null,
        refetch,

        isLoading: isPending || isFetching,
        isError,

        createTodo: createTodoMutation.mutateAsync,
        isCreating: createTodoMutation.isPending,

        updateTodo: updateTodoMutation.mutateAsync,
        isUpdating: updateTodoMutation.isPending,

        message,
        setMessage
    }
};